import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, Observer, of, Subject} from 'rxjs';
import {catchError, tap} from 'rxjs/internal/operators';

import {UserService} from './user.service';
import {NoteDataModel} from '../models/notes/note-data.model';
import {NoteCategoryDataModel} from '../models/notes/note-category-data.model';
import {EnumValidValueModel} from '../models/enum/enum-valid-value.model';
import {NoteTypeDataModel} from '../models/notes/note-type-data.model';
import {NoteGroupDataModel} from '../models/notes/note-group-data.model';
import {NoteGroupDescriptionModel} from '../models/notes/note-group-description.model';
import {PredefinedNoteSetDataModel} from '../models/notes/predefined-note-set-data.model';
import {PredefinedNoteDataModel} from '../models/notes/predefined-note-data.model';
import {OrderChangeModel} from '../models/ui/OrderChangeModel';
import {StaticDataService} from './static-data.service';
import {Globals} from '../globals';
import {TdoNoteSaveModel} from '../main-interface/controls/tdo-notes/models/tdo-note-save-model';
import {TdoNoteQueryModel} from '../main-interface/controls/tdo-notes/models/tdo-note-query.model';

@Injectable({
  providedIn: 'root'
})
export class NotesService {
  onNoteQueryRequest: Subject<TdoNoteQueryModel> = new Subject<TdoNoteQueryModel>();

  constructor(private http: HttpClient,
              private globals: Globals,
              private staticDataService: StaticDataService,
              ) {}

  noteTypeEnums: EnumValidValueModel;

  changeNoteQuery(model: TdoNoteQueryModel) {
    this.onNoteQueryRequest.next(model);
  }

  getNotes(model: TdoNoteQueryModel): Observable<NoteDataModel[]> {
    return this.http.post<NoteDataModel[]>('api/notes/get-notes', model);
  }

  getNotesForPatientCase(patientCaseID: string): Observable<NoteDataModel[]> {
    return this.http.get<NoteDataModel[]>(`api/notes/get-notes-for-patient-case/${patientCaseID}`);
  }

  getNotesForLedgerItem(LedgerID: string): Observable<NoteDataModel[]> {
    return this.http.get<NoteDataModel[]>(`api/notes/get-notes-for-ledger-item/${LedgerID}`);
  }

  getAlertNotes(patientId: string): Observable<NoteDataModel[]> {
    if(patientId == null || patientId == undefined || patientId == '' || patientId == 'null'){
      return new Observable<NoteDataModel[]>();
    }
    return this.http.get<NoteDataModel[]>(`api/notes/get-alert-notes/${patientId}`);
  }

  saveNote(noteModel: TdoNoteSaveModel): Observable<NoteDataModel> {
    return new Observable((obser: Observer<NoteDataModel>) => {
      this.http.post<NoteDataModel>('api/notes/save-note', noteModel).subscribe((res) => {
        obser.next(res);
      });
    });
  }

  testAuth(username: string, password: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        callingapp: 'TDOE',
      })
    };
    const urlEncoded = `username=${username}&password=${password}&grant_type=password`;
    return this.http.post<any>('/Token', urlEncoded, httpOptions);
  }

  getNoteGroupById(NoteGroupId: string): Observable<NoteGroupDataModel> {
    return this.http.get<NoteGroupDataModel>(`/api/notes/getNoteGroupById/${NoteGroupId}`, UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<NoteGroupDataModel>('getNoteGroupById'))
      );
  }

  getNoteGroupsByObjectId(ObjectId: string): Observable<NoteGroupDataModel[]> {
    return this.http.get<NoteGroupDataModel[]>(`/api/notes/getNoteGroupsByObjectId/${ObjectId}`,  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<NoteGroupDataModel[]>('getNoteGroupsByObjectId'))
      );
  }

  getNotesByObjectId(ObjectId: string): Observable<NoteDataModel[]> {
    return this.http.get<NoteDataModel[]>(`/api/notes/getNotesByObjectId/${ObjectId}`,  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<NoteDataModel[]>('getNotesByObjectId'))
      );
  }

  getPatientNoteObjectOptions(PatientId: string): Observable<NoteGroupDescriptionModel[]> {
    return this.http.get<NoteGroupDescriptionModel[]>(`/api/notes/getPatientNoteObjectOptions/${PatientId}`,
      UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<NoteGroupDescriptionModel[]>('getPatientNoteObjectOptions'))
      );
  }

  getNoteCountByQuery(query: any): Observable<number> {
    return this.http.post<number>('api/notes/getNoteCountByQuery', query,  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<number>('getNoteCountByQuery'))
      );
  }

  getNoteByQuery(query: any): Observable<NoteGroupDataModel[]> {
    return this.http.post<NoteGroupDataModel[]>('api/notes/getNoteByQuery', query,  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
          for (const note of _) {
            if (!note.dataNote) {
              return;
            }

            if (note.dataNote.dataNoteCategories) {
              for (const cat of note.dataNote.dataNoteCategories) {
                cat.enumNoteCategory = this.staticDataService.validValueItems.find(p => p.ValidValueItemId === cat.enumNoteCategoryID);
              }
            }

            if ( note.dataNote.dataNoteTypes) {
              for (const cat of note.dataNote.dataNoteTypes) {
                cat.enumNoteType = this.staticDataService.validValueItems.find(p => p.ValidValueItemId === cat.enumNoteTypeID);
              }
            }
          }
        }),
        catchError(this.handleError<NoteGroupDataModel[]>('getNoteByQuery'))
      );
  }

  getNoteTypeEnums(): Observable<EnumValidValueModel> {
    return this.http.get<EnumValidValueModel>('/api/notes/getNoteTypeEnums',  UserService.authorizationHeaderObject)
      .pipe(
        tap((_) => {
          this.noteTypeEnums = _;
        }),
        catchError(this.handleError<EnumValidValueModel>('getNoteTypeEnums'))
      );
  }

  getNoteCategoryEnums(): Observable<EnumValidValueModel> {
    return this.http.get<EnumValidValueModel>('/api/notes/getNoteCategoryEnums',  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<EnumValidValueModel>('getNoteCategoryEnums'))
      );
  }

  getNoteBuilderSet(): Observable<PredefinedNoteSetDataModel> {
    return this.http.get<PredefinedNoteSetDataModel>('/api/notes/getNoteBuilderSet',  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<PredefinedNoteSetDataModel>('getNoteBuilderSet'))
      );
  }

  getNoteBuilderSets(): Observable<PredefinedNoteSetDataModel[]> {
    return this.http.get<PredefinedNoteSetDataModel[]>('/api/notes/get-note-builder-sets',  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<PredefinedNoteSetDataModel[]>('get-note-builder-sets'))
      );
  }

  changePredefinedNoteSetOrder(orderChangeModel: OrderChangeModel) {
    return this.http.post<any>('api/notes/reorder-note-builder-sets', orderChangeModel)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<any>('reorder-note-builder-sets'))
      );
  }

  changePredefinedNoteOrder(orderChangeModel: OrderChangeModel) {
    return this.http.post<any>('api/notes/reorder-predefined-notes', orderChangeModel)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<any>('reorder-predefined-notes'))
      );
  }

  deletePredefinedNoteSet(note: PredefinedNoteSetDataModel) {
    return this.http.post('api/notes/delete-predefined-note-set', note)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<any>('delete-predefined-note-set'))
      );
  }

  deletePredefinedNote(note: PredefinedNoteDataModel) {
    return this.http.post('api/notes/delete-predefined-note', note)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<any>('delete-predefined-note'))
      );
  }

  getNoteBoilerplateSet(): Observable<PredefinedNoteSetDataModel> {
    return this.http.get<PredefinedNoteSetDataModel>('/api/notes/get-macro-note-set',  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<PredefinedNoteSetDataModel>('getNoteBoilerplateSet'))
      );
  }

  saveCategory(category: NoteCategoryDataModel): Observable<NoteCategoryDataModel> {
    return this.http.post<NoteCategoryDataModel>('/api/notes/saveCategory', category,  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<NoteCategoryDataModel>('saveCategory'))
      );
  }

  saveType(type: NoteTypeDataModel): Observable<NoteTypeDataModel> {
    return this.http.post<NoteTypeDataModel>('/api/notes/saveType', type,  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<NoteTypeDataModel>('saveType'))
      );
  }

  saveNoteGroup(noteGroup: NoteGroupDataModel): Observable<NoteGroupDataModel> {
    return this.http.post<NoteGroupDataModel>('/api/notes/saveNoteGroup', noteGroup,  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<NoteGroupDataModel>('saveNoteGroup'))
      );
  }

  saveCategoryToNote(noteCategory: NoteCategoryDataModel): Observable<NoteCategoryDataModel> {
    return this.http.post<NoteCategoryDataModel>('/api/notes/saveDataNoteCategory', noteCategory,  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<NoteCategoryDataModel>('saveNoteCategory'))
      );
  }

  savePredefinedNoteBuilderSet(set: PredefinedNoteSetDataModel) {
    return this.http.post<PredefinedNoteSetDataModel>('api/notes/save-note-builder-set', set)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<PredefinedNoteSetDataModel>('save-note-builder-set'))
      );
  }

  savePredefinedNoteBuilderNote(note: PredefinedNoteDataModel) {
    return this.http.post<PredefinedNoteDataModel>('api/notes/save-note-builder-note', note)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<PredefinedNoteDataModel>('save-note-builder-note'))
      );
  }

  savePredefinedNotes(predefinedNotes: PredefinedNoteDataModel[]): Observable<PredefinedNoteDataModel[]> {
    return this.http.post<PredefinedNoteDataModel[]>('/api/notes/savePredefinedNotes', predefinedNotes,
      UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<PredefinedNoteDataModel[]>('savePredefinedNotes'))
      );
  }

  saveDataNote(note: NoteDataModel): Observable<NoteDataModel> {
    return this.http.post<NoteDataModel>('/api/notes/saveDataNote', note,  UserService.authorizationHeaderObject)
      .pipe(
        tap(_ => {
        }),
        catchError(this.handleError<NoteDataModel>('saveDataNote'))
      );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any) => {
      console.error(error);
      return of(result as T);
    };
  }
}
